<template class="z-50">
    <div class="modal-overlay" @click="closeModal">
        <div v-if="!succes" class="modal relative bg-green-300" @click.stop>
            <p class="p-0 text-xl font-semibold">Reserveringsverzoek</p>
            <div @click="closeModal" class="close absolute right-5 top-4">X</div>
            <div class="flex flex-col">
                <div class="grid grid-cols-2 gap-4">
                    <div class="mb-4">
                        <label for="firstName" class="block font-mori text-sm font-medium text-gray-700"
                            >Voornaam</label
                        >
                        <input
                            v-model="firstName"
                            type="text"
                            id="firstName"
                            class="mt-1 block w-full border px-3 py-2" />
                        <p v-if="formSubmitted && !validFirstname" class="text-sm text-red-600">Voornaam is vereist</p>
                    </div>

                    <div class="mb-4">
                        <label for="lastName" class="block font-mori text-sm font-medium text-gray-700"
                            >Achternaam</label
                        >
                        <input
                            v-model="lastName"
                            type="text"
                            id="lastName"
                            class="mt-1 block w-full border px-3 py-2" />
                        <p v-if="formSubmitted && !validLastname" class="text-sm text-red-600">Achternaam is vereist</p>
                    </div>
                </div>

                <div class="mb-4">
                    <label for="email" class="block font-mori text-sm font-medium text-gray-700">Email</label>
                    <input v-model="email" type="email" id="email" class="mt-1 block w-full border px-3 py-2" />
                    <p v-if="formSubmitted && !validEmail" class="text-sm text-red-600">
                        Een geldig emailadres is vereist
                    </p>
                </div>

                <div class="mb-5 last:!mb-0" v-for="variant in variants">
                    <div v-if="variant.translated.name != 'Kleur' && variant.translated.name != 'Lengtemaat'">
                        <span
                            v-if="variant.translated.name == 'Maat'"
                            class="block font-mori text-sm font-medium text-gray-700"
                            >{{ variant.translated.name }}</span
                        >
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="option in variant.options"
                                :key="variant.id"
                                class="size-[50px] bg-green-400 text-sm font-semibold duration-200 hover:bg-green-500"
                                :class="{
                                    'pointer-events-none !bg-green-600': isSelected(option),
                                    'pointer-events-none !bg-green-200 text-green-500 opacity-40': option.stock === 0,
                                }"
                                @click="selectOption(option)">
                                {{ option.translated.name }}
                            </button>
                        </div>
                        <p v-if="formSubmitted && !validVariant" class="text-sm text-red-600">Selecteer jouw maat</p>
                    </div>
                </div>

                <div class="mt-4 flex justify-end">
                    <button
                        @click="submitRequest"
                        class="btn btn-primary mb-2 !flex w-full items-center justify-center">
                        Reserveer
                    </button>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="modal relative bg-green-300" @click.stop>
                <p class="p-0 text-base font-semibold">Reserveringsverzoek</p>
                <div @click="closeModal" class="close absolute right-5 top-4">X</div>
                <p>Bedankt voor je reserveringsverzoek. We nemen zo snel mogelijk contact met je op.</p>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';

    const props = defineProps({
        product: Object,
        variants: Array,
    });

    const emit = defineEmits(['close']);

    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const variant = ref('');
    const formSubmitted = ref(false);
    const succes = ref(false);

    const validFirstname = computed(() => firstName.value.trim() !== '');
    const validLastname = computed(() => lastName.value.trim() !== '');
    const validEmail = computed(() => /\S+@\S+\.\S+/.test(email.value));
    const validVariant = computed(() => variant.value.trim() !== '');

    const selectedOptions = ref([]);

    const isSelected = option => {
        return selectedOptions.value.some(opt => opt.id === option.id);
    };

    const findVariant = async () => {
        const id = await useFindVariant(props.product.id, selectedOptions.value);
        return id;
    };

    const selectOption = option => {
        const existingOption = selectedOptions.value.find(opt => opt.groupId === option.groupId);
        if (existingOption) {
            const index = selectedOptions.value.indexOf(existingOption);
            selectedOptions.value.splice(index, 1, option);
        } else {
            selectedOptions.value.push(option);
        }
        variant.value = selectedOptions.value.map(opt => opt.id).join(',');
    };

    const formValid = computed(
        () => validFirstname.value && validLastname.value && validEmail.value && validVariant.value,
    );

    const submitRequest = async () => {
        formSubmitted.value = true;

        if (formValid.value) {
            try {
                const variantId = await findVariant();
                const response = await fetch('/api/reservationRequest', {
                    method: 'POST',
                    body: JSON.stringify({
                        firstName: firstName.value,
                        lastName: lastName.value,
                        email: email.value,
                        variant: variantId,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'An error occurred while submitting the request.');
                }

                if (response.ok) {
                    succes.value = true;
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    const closeModal = () => {
        emit('close');
        document.body.style.overflow = 'auto';
    };
</script>

<style scoped>
    .modal-overlay {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.8);
        z-index: 50;
    }

    .modal {
        padding: 40px;
        width: 500px;
        max-height: 90vh;
        max-width: 90vw;
        overflow-y: auto;
    }

    .close {
        cursor: pointer;
        font-size: 20px;
    }

    p {
        font-size: 16px;
        margin-bottom: 20px;
    }
</style>
